import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Box, Grid, Tooltip } from "@material-ui/core";
import PerfectScrollbar from "react-perfect-scrollbar";

import { makeStyles } from "@material-ui/core/styles";

import AppColor from "utilities/AppColor";
import moment from "moment";
import { useHistory } from "react-router-dom";
import { Skeleton } from "@material-ui/lab";
import { useMedia } from "react-use";
import { useDispatch } from "react-redux";
import { fetchJobDetail } from "store/slices/job";
import { getWorkerApplied } from "store/slices/job";
import { BILLING_TYPE, POST_JOB } from "config/AppConfig";
import BookingWorkerModal from "views/Client/Job/Components/BookingWorkerModal";
import AppDialog from "components/Dialog/AppDialog";

const BOOKING_COLOR = {
  'BOOKED': { color: AppColor.green },
  'ON_GOING': { color: AppColor.violet },
  'CANCEL': { color: AppColor.red },
  'DONE': { color: AppColor.mainBlue },
  'WAITING': { color: AppColor.orange },
  'NO_CLOCK_IN': { color: AppColor.yellow },
  'NO_ACCEPTED': { color: AppColor.cinnabar },
  'EXPRESS_INTEREST': { color: AppColor.pink }
};

const useStyles = makeStyles(() => ({
  customScrollbar: {
    maxHeight: 600,
    display: "flex",
  },
  customScrollbarTable: {
    width: '100%',
  },
  wrapCenter: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerLeft: {
    borderTopLeftRadius: '10px'
  },
  headerRight: {
    borderLeft: 'solid 1px #d2d2d2',
    borderRight: 'solid 1px #d2d2d2',
    borderTopRightRadius: '10px',
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
  },
  headerItem: {
    fontWeight: 800,
    borderTop: 'solid 1px #d2d2d2',
    borderBottom: 'solid 1px #d2d2d2',
    borderLeft: 'solid 1px #d2d2d2',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0.8rem 0',
  },
  bodyLeft: {
    borderBottom: 'solid 1px #d2d2d2',
    borderLeft: 'solid 1px #d2d2d2',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 10,
    color: AppColor.mainColor,
    fontWeight: 800,
  },
  bodyRight: {
    borderBottom: 'solid 1px #d2d2d2',
    borderLeft: 'solid 1px #d2d2d2',
    borderRight: 'solid 1px #d2d2d2',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    paddingLeft: 10,
    fontWeight: 800,
  },
  bodyItem: {
    minHeight: 79,
    borderLeft: 'solid 1px #d2d2d2',
    borderBottom: 'solid 1px #d2d2d2',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: '0 3px',
  },
  footerLeft: {
    borderBottomLeftRadius: '10px',
  },
  footerRight: {
    borderRight: 'solid 1px #d2d2d2',
    borderBottomRightRadius: '10px',
  },
  footerItem: {
    fontWeight: 800,
    backgroundColor: '#f5f5f5',
    borderLeft: 'solid 1px #d2d2d2',
    borderTop: 'solid 1px #d2d2d2',
    borderBottom: 'solid 1px #d2d2d2',
    display: 'flex',
    alignItems: 'center',
    padding: '0.8rem 10px',
  },
  skeletonLeft: {
    borderTop: 'solid 1px #d2d2d2',
    borderBottom: 'solid 1px #d2d2d2',
    borderLeft: 'solid 1px #d2d2d2',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 10,
    color: AppColor.mainColor,
    fontWeight: 800
  },
  skeletonRight: {
    borderTop: 'solid 1px #d2d2d2',
    borderBottom: 'solid 1px #d2d2d2',
    borderLeft: 'solid 1px #d2d2d2',
    borderRight: 'solid 1px #d2d2d2',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    paddingLeft: 10,
    fontWeight: 800,
  },
  skeletonItem: {
    borderTop: 'solid 1px #d2d2d2',
    borderLeft: 'solid 1px #d2d2d2',
    borderBottom: 'solid 1px #d2d2d2',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: '0 3px',
  },

  jobCalendarContainer: {
    fontSize: 13,
    cursor: "pointer",
    border: 'solid 1px #d2d2d2',
    // height: 'calc(100% - 1px)',
    borderRadius: '3px',
    margin: '3px 0',
    width: '100%',
    '&.BOOKED': {
      backgroundColor: BOOKING_COLOR.BOOKED.color
    },
    '&.CANCEL': {
      backgroundColor: BOOKING_COLOR.CANCEL.color
    },
    '&.DONE': {
      backgroundColor: BOOKING_COLOR.DONE.color
    },
    '&.ON_GOING': {
      backgroundColor: BOOKING_COLOR.ON_GOING.color
    },
    '&.WAITING': {
      backgroundColor: BOOKING_COLOR.WAITING.color
    },
    '&.NO_CLOCK_IN': {
      backgroundColor: BOOKING_COLOR.NO_CLOCK_IN.color
    },
    '&.NO_ACCEPTED': {
      backgroundColor: BOOKING_COLOR.NO_ACCEPTED.color
    },
    '&.EXPRESS_INTEREST': {
      backgroundColor: BOOKING_COLOR.EXPRESS_INTEREST.color
    },
    '&.AVAILABLE': {
      color: '#000'
    }
  },
  jobCalendar: {
    display: "flex",
    justifyContent: 'space-around',
    flexDirection: "column",
    color: '#fff',
    padding: '0 5px',
  },
  jobAvailableCalendar: {
    display: "flex",
    justifyContent: 'space-around',
    flexDirection: "column",
    color: '#000',
    padding: '0 5px',
    border: '1px solid #2fff3f'
  },
  svgSmall: {
    width: 16,
    height: 16,
  },
  svgNormal: {
    width: 22,
    height: 22,
  },
  itemIconShift: {
    maskSize: '100%',
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    maskPosition: 'center',
    backgroundColor: "#fff",
  },
  bodyNodata: {
    height: 80,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderLeft: 'solid 1px #d2d2d2',
    borderRight: 'solid 1px #d2d2d2',
  },
}));
const offsetLazyload = 200;

const defaultWorkHistory = {
  // dayOfWeek: DAYS_OF_WEEK[0],
  isNew: true,
  shiftType: "",
  startTime: moment(new Date()).hours(0).minutes(0).format("YYYY/MM/DD HH:mm"),
  endTime: moment(new Date()).hours(0).minutes(0).format("YYYY/MM/DD HH:mm"),
  numberWorkers: "",
  numberDayOfWeek: 0,
  isExpand: true,
  groupByDayOfWeek: {},
  segments: []
};
const COUNT_WEEK_DAY = {
  'SUNDAY': 0,
  'MONDAY': 1,
  'TUESDAY': 2,
  'WEDNESDAY': 3,
  'THURSDAY': 4,
  'FRIDAY': 5,
  'SATURDAY': 6
};

const CalendarTableShifts = (props) => {
  const {
    firstWeekDay,
    dataList,
    loadingPage,
    totalPages,
    page,
    loadData
  } = props;
  const { t } = useTranslation();
  const classes = useStyles();
  const below768 = useMedia('(max-width: 767.2px)');
  const history = useHistory();
  const dispatch = useDispatch();

  const [totalHour, setTotalHour] = useState({
    monday: 0,
    tuesday: 0,
    wednesday: 0,
    thursday: 0,
    friday: 0,
    saturday: 0,
    sunday: 0,
    total: 0,
  });

  const [selectedWeek, setSelectedWeek] = useState({});
  const [jobId, setJobId] = useState();
  const [shiftType, setShiftType] = useState();
  const [dayOfWeek, setDayOfWeek] = useState();
  const [week, setWeek] = useState();
  const [yearJobCalendar, setYearJobCalendar] = useState();
  const [shiftId, setShiftId] = useState();
  const [reload, setReload] = useState(false);
  const [showBookingWorker, setShowBookingWorker] = useState(false);
  const [jobAvailabilityList, setJobAvailabilityList] = useState([]);
  const [initializeData, setInitializeData] = useState({
    clientId: "",
    title: "",
    description: "",
    address1: "",
    address2: "",
    postalCode: "",
    department: "",
    floor: "",
    accreditationId: "",
    specialtyId: "",
    supportId: "",
    coordParticipantId: "",
    yearsOfExperience: "",
    startDate: "",
    endDate: "",
    jobAvailabilityRequests: [defaultWorkHistory],
    isPostJob: POST_JOB.POST_JOB,
    phone: "",
    status: '',

    planManagerEmail: "",
    planManagerOrganizationName: "",
    planManagerPhone: "",
    billToAddress: "",
    billingDetail: "",
  });


  const getNumberByShift = (shiftType) => {
    switch (shiftType) {
      case 'MORNING':
        return 1;
      case 'AFTERNOON':
        return 2;
      case 'EVENING':
        return 3;
      case 'NIGHT':
        return 4;
      default:
        return 0;
    }
  };
  const sortByShiftType = (data) => {
    return data.sort(function (a, b) {
      const keyA = getNumberByShift(a.shiftType);
      const keyB = getNumberByShift(b.shiftType);
      // Compare the 2 number
      if (keyA < keyB) return -1;
      if (keyA > keyB) return 1;
      const startTimeA = moment(a.startTime);
      const startTimeB = moment(b.startTime);
      return parseFloat(moment.range(startTimeA, startTimeB).diff("minutes")) > 0 ? -1 : 1;
    });
  };

  const handleScrollBody = (e) => {
    if (page + 1 <= totalPages && !loadingPage) {
      const element = e.target;
      if (element && element.scrollTop + element.clientHeight + offsetLazyload >= element.scrollHeight) {
        loadData({ page_number: page + 1 });
      }
    }
  };

  const renderBodyItem = (list) => {
    if (!list) {
      return null;
    }
    return sortByShiftType(list).map(item => {
      const { id, jobId, shiftType, startTime, endTime, worker, bookingStatus } = item;
      const safeWorker = worker || {};
      const isCancelledStatus = bookingStatus === 'CANCEL' || bookingStatus === 'REJECTED';
      const hasActiveBooking = bookingStatus && !isCancelledStatus;
      return (
        <div key={`body-shift-item-${id}`}
          className={`${classes.jobCalendarContainer} ${bookingStatus ? bookingStatus : 'AVAILABLE'}`}
          onClick={async () => {
            await _loadJobDetail(jobId)
            if (bookingStatus) {
              history.push(`/admin/booking/${id}`)
            } else {
              setJobId(jobId);
              setShiftType(item.shiftType);
              setDayOfWeek(item.dayOfWeek);
              setWeek(moment(firstWeekDay).week());
              setYearJobCalendar(moment(firstWeekDay).isoWeekYear());
              console.log("moment ", moment(firstWeekDay).isoWeekYear());
              setShiftId(item.id);
              setShowBookingWorker(true);
            }
          }
          }
        >
          <div className={`${hasActiveBooking ? classes.jobCalendar : classes.jobAvailableCalendar}`}>
            <Box className='text-center' style={{ display: 'flex', justifyContent: 'center' }}>
              <span style={{ fontWeight: 'bold', fontSize: below768 ? 12 : '' }}>{`${moment(startTime).format('HH:mm')} - ${moment(endTime).format('HH:mm')}`}</span>
            </Box>
            <span style={{ marginLeft: 5 }}>{t(`shift.${shiftType}`)}</span>
            {hasActiveBooking ? <Tooltip title={`${safeWorker.firstName || ''} ${safeWorker.lastName || ''}`} placement="top">
              <span style={{ width: '100%', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', marginLeft: 5 }}>
                {`${safeWorker.firstName || ''} ${safeWorker.lastName || ''}`}
              </span>
            </Tooltip> : <span style={{ width: '100%', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', marginLeft: 5 }}>
              {isCancelledStatus ? t(`bookingStatus.${bookingStatus}`) : 'Not Booked'}
            </span>}
          </div>
        </div>
      )
    })
  };

  const renderSkeleton = () => {
    return [...Array(5).keys()].map(key => (
      <React.Fragment key={`container-skeleton-${key}`}>
        <Grid item className={classes.skeletonLeft} style={{ width: '15%', paddingLeft: 10 }}>
          <Skeleton variant="text" width={50} />
        </Grid>
        {
          [...Array(7).keys()].map(index => {
            const number = Math.floor(Math.random() * 11);
            return (
              <Grid key={`grid-skeleton-${index}`} item className={classes.skeletonItem} style={{ width: '11%', minHeight: 106 }} >
                {
                  number % 2 !== 0 &&
                  <div className={classes.jobCalendarContainer}>
                    <Skeleton variant="rect" width={'100%'} height={96} />
                  </div>
                }
              </Grid>
            );
          })
        }

        <Grid item className={classes.skeletonRight} style={{ width: '8%' }} >
          <Skeleton variant="text" width={50} />
        </Grid>
      </React.Fragment>
    ))
  };

  useEffect(() => {
    if (dataList) {
      var monday = 0;
      var tuesday = 0;
      var wednesday = 0;
      var thursday = 0;
      var friday = 0;
      var saturday = 0;
      var sunday = 0;
      for (const i in dataList) {
        const bookingResponseList = dataList[i].bookingResponseList || [];
        bookingResponseList
          .forEach(item => {
            if (item.bookingStatus === 'DONE' && item.realEndTime && item.realStartTime) {
              let dateStartTime = moment(item.realStartTime);
              let dateEndTime = moment(item.realEndTime);
              const number = parseFloat(moment(dateEndTime).diff(moment(dateStartTime), "hours", true).toFixed(2));
              switch (item.dayOfWeek) {
                case 'MONDAY':
                  monday += number;
                  break;
                case 'TUESDAY':
                  tuesday += number;
                  break;
                case 'WEDNESDAY':
                  wednesday += number;
                  break;
                case 'THURSDAY':
                  thursday += number;
                  break;
                case 'FRIDAY':
                  friday += number;
                  break;
                case 'SATURDAY':
                  saturday += number;
                  break;
                case 'SUNDAY':
                  sunday += number;
                  break;
                default:
                  break;
              }
            }
          });
      }

      const total = (monday + tuesday + wednesday + thursday + friday + saturday + sunday);
      setTotalHour({ monday, tuesday, wednesday, thursday, friday, saturday, sunday, total });
    }

  }, [dataList]);

    // common Function use UTC
    const isApplyTime = (monday, sunday, timeApply) => {
      const applyStartDate = moment.utc(timeApply.applyStartDate);
      const applyEndDate = moment.utc(timeApply.applyEndDate);
      return (moment.range(monday, applyEndDate).diff('minutes') > 0 && moment.range(applyStartDate, sunday).diff('minutes') > 0);
    };
    
    const loadWorkersApplied = async (jobId) => {
      const result = await dispatch(getWorkerApplied({ jobId: jobId }));
      if (result.payload) {
        // setWorkersApplied(result.payload);
      } else {
        history.push("/admin/jobs");
      }
    };
    const setupSegmentList = (list = [], startDate, minTime) => {
      return list.map((item) => {
        const clone = { ...item };
        const momentStart = moment(item.startTime);
        const momentEnd = moment(item.endTime);
        const newStart = moment(startDate).hours(momentStart.hours()).minutes(momentStart.minutes());
        const newEnd = moment(startDate).hours(momentEnd.hours()).minutes(momentEnd.minutes());
        if (moment.range(minTime, newStart).diff('minutes') < 0) {
          newStart.add(1, 'days');
        }
        if (moment.range(minTime, newEnd).diff('minutes') <= 0) {
          newEnd.add(1, 'days');
        }
        clone["startTime"] = newStart.format("YYYY/MM/DD HH:mm:ss");
        clone["endTime"] = newEnd.format("YYYY/MM/DD HH:mm:ss");
        clone["isExpand"] = false;
        clone["status"] = 'ACTIVE';
        return clone;
      });
    };

  const sortAvailabilities = (a, b) => {
    // Sort by shift
    const aIsApplyTime = isApplyTime(moment.utc().startOf('isoWeek'), moment.utc().endOf('isoWeek'), a);
    const bIsApplyTime = isApplyTime(moment.utc().startOf('isoWeek'), moment.utc().endOf('isoWeek'), b);
    if (aIsApplyTime !== bIsApplyTime) {
      return aIsApplyTime ? -1 : 1;
    }
    const keyA = getNumberByShift(a.shiftType);
    const keyB = getNumberByShift(b.shiftType);
    if (keyA < keyB) return -1;
    if (keyA > keyB) return 1;
    // Sort by Time
    const startTimeA = moment(a.startTime);
    const startTimeB = moment(b.startTime);
    return parseFloat(moment.range(startTimeA, startTimeB).diff("minutes")) > 0 ? -1 : 1;
  };

    // isApplyNextWeek === false => it only applies to the current week.
    const setupAvailability = (jobAvailability, jobAvailabilityNextWeek, startDate, endDate) => {
      const clone = { ...jobAvailability };
      const { startTime, endTime, parentId, applyEndDate } = jobAvailability;
      let isApply = isApplyTime(moment.utc().startOf('isoWeek'), moment.utc().endOf('isoWeek'), jobAvailability);
      if (!parentId && parentId !== 0 && moment().hours(0).minutes(0).seconds(0).milliseconds(0).isBefore(moment(startDate))) {
        isApply = true;
      }
  
      let isApplyNextWeek = (moment.utc(applyEndDate).endOf('isoWeek').format("YYYY/MM/DD") === moment(endDate).endOf('isoWeek').format("YYYY/MM/DD"));
      if (isApply) {
        // const jobAvailabilityNextWeek = newjobAvailabilitiesList.find(subItem => subItem.parentId === availability.id);
        if (jobAvailabilityNextWeek) {
          const startTimeNextWeek = moment(startDate).hours(moment(jobAvailabilityNextWeek.startTime).hours()).minutes(moment(jobAvailabilityNextWeek.startTime).minutes());
          const endTimeNextWeek = moment(startDate).hours(moment(jobAvailabilityNextWeek.endTime).hours()).minutes(moment(jobAvailabilityNextWeek.endTime).minutes());
          if (moment.range(startTimeNextWeek, endTimeNextWeek).diff('minutes') <= 0) {
            endTimeNextWeek.add(1, 'days');
          }
          clone["startTimeNextWeek"] = startTimeNextWeek.format("YYYY/MM/DD HH:mm");
          clone["endTimeNextWeek"] = endTimeNextWeek.format("YYYY/MM/DD HH:mm");
          // setting of next week
          clone['settingNextWeek'] = {
            startTime: startTimeNextWeek.format("YYYY/MM/DD HH:mm"),
            endTime: endTimeNextWeek.format("YYYY/MM/DD HH:mm"),
            applyStartDate: jobAvailabilityNextWeek.applyStartDate,
            applyEndDate: jobAvailabilityNextWeek.applyEndDate,
            numberOfBooked: jobAvailabilityNextWeek.numberOfBooked ? jobAvailabilityNextWeek.numberOfBooked : 0,
            totalBookings: jobAvailabilityNextWeek.totalBookings ? jobAvailabilityNextWeek.totalBookings : 0,
            status: 'ACTIVE',
            isApply: false,
            isApplyNextWeek: true,
            segments: setupSegmentList(jobAvailabilityNextWeek.segmentList, startDate, startTimeNextWeek),
          };
        } else {
          clone["startTimeNextWeek"] = startTime;
          clone["endTimeNextWeek"] = endTime;
        }
        clone["startTimeCurrentWeek"] = startTime;
        clone["endTimeCurrentWeek"] = endTime;
      }
      clone['isApply'] = isApply;
      clone['isApplyNextWeek'] = isApplyNextWeek;
      clone['segments'] = setupSegmentList(jobAvailability.segmentList, startDate, startTime);
      clone['groupByDayOfWeek'] = {};
      delete clone.id;
      delete clone.segmentList;
      delete clone.dayOfWeek;
      return { ...clone };
    };

  // Group data by startTime, endTime, shiftType, numberWorkers
  const groupAvailability = (list = [], startDate, endDate) => {
    const listMap = new Map();
    const groupAvailability = [];
    const idsNextWeek = {};
    var countGroupShiftId = 0;
    var sortList = [...list];
    if (sortList.length > 1) {
      sortList.sort(sortAvailabilities);
    }

    sortList.forEach((item) => {
      var clone = { ...item };
      const momentStart = moment(item.startTime);
      const momentEnd = moment(item.endTime);
      const newStart = moment(startDate).hours(momentStart.hours()).minutes(momentStart.minutes());
      const newEnd = moment(startDate).hours(momentEnd.hours()).minutes(momentEnd.minutes());
      if (moment.range(newStart, newEnd).diff('minutes') <= 0) {
        newEnd.add(1, 'days');
      }
      const key = `${momentStart.format("YYYY/MM/DD-HH:mm")}-${momentEnd.format("YYYY/MM/DD-HH:mm")}-${item.shiftType}-${item.numberWorkers}`;
      // check it is not Availability of next week
      if (!idsNextWeek[item.id]) {
        // find jobAvailability Next Week of this item
        const jobAvailabilityNextWeek = list.filter(subItem => subItem.parentId === item.id)[0];

        if (listMap.has(key)) {
          // old group
          clone = listMap.get(key);
          if ((item.numberOfBooked || item.numberOfBooked === 0) && clone.numberOfBooked || clone.numberOfBooked === 0) {
            clone['numberOfBooked'] += item.numberOfBooked;
          }
          if ((item.totalBookings || item.totalBookings === 0) && clone.totalBookings || clone.totalBookings === 0) {
            clone['totalBookings'] += item.totalBookings;
          }
          if (jobAvailabilityNextWeek && clone['settingNextWeek']) {
            // add numberOfBooked of jobAvailabilityNextWeek
            if ((jobAvailabilityNextWeek.numberOfBooked || jobAvailabilityNextWeek.numberOfBooked === 0) && clone.numberOfBooked || clone.numberOfBooked === 0) {
              clone['settingNextWeek'].numberOfBooked += jobAvailabilityNextWeek.numberOfBooked;
              // clone['numberOfBooked'] += jobAvailabilityNextWeek.numberOfBooked;
            }
            // add totalBookings of jobAvailabilityNextWeek
            if ((jobAvailabilityNextWeek.totalBookings || jobAvailabilityNextWeek.totalBookings === 0) && clone.totalBookings || clone.totalBookings === 0) {
              clone['settingNextWeek'].totalBookings += jobAvailabilityNextWeek.totalBookings;
              // clone['totalBookings'] += jobAvailabilityNextWeek.totalBookings;
            }
          }
        } else {
          // new group
          clone["startTime"] = newStart.format("YYYY/MM/DD HH:mm");
          clone["endTime"] = newEnd.format("YYYY/MM/DD HH:mm");
          clone = setupAvailability(clone, jobAvailabilityNextWeek, startDate, endDate);
          // count group
          if (clone.isApply) {
            countGroupShiftId += 1;
          }
        }

        // set data for this dayOfWeek
        clone.groupByDayOfWeek[item.dayOfWeek] = {
          id: item.id,
          status: item.status,
          isBooked: !!(item.numberOfBooked && item.numberOfBooked > 0)
        };
        if (jobAvailabilityNextWeek) {
          idsNextWeek[jobAvailabilityNextWeek.id] = true;
          // add idNextWeek for group day of week
          clone.groupByDayOfWeek[item.dayOfWeek].idNextWeek = jobAvailabilityNextWeek.id;
          // set isBooked if jobAvailabilityNextWeek has booked
          if (jobAvailabilityNextWeek.numberOfBooked && jobAvailabilityNextWeek.numberOfBooked > 0) {
            clone.groupByDayOfWeek[item.dayOfWeek].isBooked = true;
          }
        }
        listMap.set(key, clone);
      }
      // else {
      //   if (listMap.has(key)) {
      //     clone = listMap.get(key)
      //     if ((item.numberOfBooked || item.numberOfBooked === 0) && clone.numberOfBooked || clone.numberOfBooked === 0) {
      //       clone['numberOfBooked'] += item.numberOfBooked;
      //     }
      //     if ((item.totalBookings || item.totalBookings === 0) && clone.totalBookings || clone.totalBookings === 0) {
      //       clone['totalBookings'] += item.totalBookings;
      //     }
      //   }
      // }
    });
    listMap.forEach(item => groupAvailability.push({ ...item, countGroupShiftId }));
    return groupAvailability;
  };

  const getNumberDayOfWeek = ({ startDate, endDate, applyStartDate, applyEndDate, dayOfWeekList }) => {
    let count = 0;
    let dateFrom = startDate;
    let dateTo = endDate;
    if (applyStartDate && moment.range(moment.utc(applyStartDate), moment.utc(startDate)).diff('minutes') < 0) {
      dateFrom = moment.utc(applyStartDate).format("YYYY/MM/DD");
    }
    if (applyEndDate) {
      dateTo = moment.utc(applyEndDate).format("YYYY/MM/DD");
    }
    const momentStartDate = moment(dateFrom);
    const momentEndDate = moment(dateTo);
    while (momentStartDate.isSameOrBefore(momentEndDate)) {
      for (const i in dayOfWeekList) {
        if (momentStartDate.weekday() === COUNT_WEEK_DAY[dayOfWeekList[i]]) {
          count++;
          break;
        }
      }
      momentStartDate.add(1, 'day');
    }
    return count;
  };

  const _loadJobDetail = async (jobId) => {
    // setIsLoading(true);
    const result = await dispatch(fetchJobDetail({ id: jobId }));
    const { payload } = result;
    if (payload) {
      // let isCheckBooked = false;
      const data = { ...initializeData };
      const {
        jobAvailabilities = [],
        startDate = "",
        endDate = "",
        isPostJob,
        specialty = {},
        accreditation = {},
        support = {},
        client = {},
        yearsOfExperience,
        isAllowDelete,
        isAllowEndJob
      } = payload;

      Object.keys(initializeData).forEach((key) => {
        data[key] = payload[key] || "";
      });

      const newjobAvailabilitiesList = groupAvailability(jobAvailabilities, startDate, endDate);
      data["jobAvailabilityRequests"] = newjobAvailabilitiesList
        .map((item) => {
          const clone = { ...item };
          const { numberOfBooked, applyStartDate, applyEndDate, groupByDayOfWeek } = item;
          if (numberOfBooked > 0) {
            // isCheckBooked = true;
          }
          clone["numberDayOfWeek"] = getNumberDayOfWeek({ startDate, endDate, applyStartDate, applyEndDate, dayOfWeekList: Object.keys(groupByDayOfWeek) });
          return clone;
        });
      data["specialtyId"] = specialty.id;
      if (client) {
        data["clientType"] = client.clientType;
        data["clientId"] = client.id;
        data["client"] = client;
      }
      if (client.id) {
        data["billingDetail"] = client.billingDetail;
        data["billToAddress"] = client.billToAddress;
        if (client.billingDetail === BILLING_TYPE.PLAN_MANAGER.id) {
          data["planManagerOrganizationName"] = client.planManagerOrganizationName;
          data["planManagerPhone"] = client.planManagerPhone;
          data["planManagerEmail"] = client.planManagerEmail;
        }
      }

      data["isPostJob"] = isPostJob ? POST_JOB.POST_JOB : POST_JOB.UNPOST_JOB;
      data["accreditationId"] = accreditation.id;
      data["supportId"] = support.id;
      data["startDate"] = moment(startDate).format("YYYY/MM/DD");
      data["endDate"] = moment(endDate).format("YYYY/MM/DD");
      data["yearsOfExperience"] = yearsOfExperience;
      data["isAllowDelete"] = isAllowDelete;
      data["isAllowEndJob"] = isAllowEndJob;
      if (moment.range(moment(), moment(startDate)).diff('minutes') > 0) {
        // setMinEndDate(moment(startDate).add(1, 'days').format("YYYY/MM/DD"))
      } else {
        // setMinEndDate(moment().add(1, 'days').format("YYYY/MM/DD"))
      }
      setInitializeData(data);
      // setJobType(payload.jobType);
      setJobAvailabilityList(jobAvailabilities);
      // setIsBooked(isCheckBooked);
      loadWorkersApplied(jobId);
      if (payload.jobType === 'SINGLE_WORKER') {
        return true;
      }
    } else {
      // setIsLoading(false);
      history.push("/admin/jobs");
    }
    // setIsLoading(false);
  };

  const handleReloadJob = () => {
    _loadJobDetail(jobId);
    loadData({ page_number: 1 });
    setReload(false);
  }

  const handleGoToDetail = () => {
    history.push(`/admin/jobs/detail/${jobId}`);
  }

  useEffect(() => {
    if (reload) {
      handleReloadJob();
    }
  }, [reload]);

  return (
    <div style={{ fontSize: below768 ? 13 : '' }}>
      <PerfectScrollbar
        className={`user-console ${classes.customScrollbarTable}`}
        options={{
          suppressScrollX: false,
          suppressScrollY: true
        }}
        id="table-calendar-shift">
        <div style={{ minWidth: '912px' }}>
          <Grid container style={{ margin: '20px 0' }}>
            {/* Header */}
            <Grid item className={`${classes.headerItem} ${classes.headerLeft}`} style={{ width: '15%' }} >{t(`label.participant`)}</Grid>
            <Grid item className={classes.headerItem} style={{ width: '11%' }} >{firstWeekDay.format('ddd Do')}</Grid>
            <Grid item className={classes.headerItem} style={{ width: '11%' }} >{firstWeekDay.clone().add(1, 'days').format('ddd Do')}</Grid>
            <Grid item className={classes.headerItem} style={{ width: '11%' }} >{firstWeekDay.clone().add(2, 'days').format('ddd Do')}</Grid>
            <Grid item className={classes.headerItem} style={{ width: '11%' }} >{firstWeekDay.clone().add(3, 'days').format('ddd Do')}</Grid>
            <Grid item className={classes.headerItem} style={{ width: '11%' }} >{firstWeekDay.clone().add(4, 'days').format('ddd Do')}</Grid>
            <Grid item className={classes.headerItem} style={{ width: '11%' }} >{firstWeekDay.clone().add(5, 'days').format('ddd Do')}</Grid>
            <Grid item className={classes.headerItem} style={{ width: '11%' }} >{firstWeekDay.clone().add(6, 'days').format('ddd Do')}</Grid>
            <Grid item className={`${classes.headerItem} ${classes.headerRight}`} style={{ width: '8%' }} >{t('label.weeklyTotal')}</Grid>

            {/* Body */}
            <Grid item xs={12} style={{ position: 'relative' }}>
              <PerfectScrollbar
                onScroll={handleScrollBody}
                className={`user-console ${classes.customScrollbar}`}
                options={{
                  suppressScrollX: true,
                  suppressScrollY: false,
                }}
                id="body-calendar-shift">
                <Grid container>
                  {
                    loadingPage !== 1 && dataList && dataList.map((bookingByParticipant, index) => {
                      const jobAvailable = bookingByParticipant.jobAvailabilityResponseList || [];
                      const bookingResponseList = bookingByParticipant.bookingResponseList || [];
                      const monday = [...bookingResponseList.filter(item => item.dayOfWeek === 'MONDAY'), ...jobAvailable.filter(item => item.dayOfWeek === 'MONDAY')];
                      const tuesday = [...bookingResponseList.filter(item => item.dayOfWeek === 'TUESDAY'), ...jobAvailable.filter(item => item.dayOfWeek === 'TUESDAY')];
                      const wednesday = [...bookingResponseList.filter(item => item.dayOfWeek === 'WEDNESDAY'), ...jobAvailable.filter(item => item.dayOfWeek === 'WEDNESDAY')];
                      const thursday = [...bookingResponseList.filter(item => item.dayOfWeek === 'THURSDAY'), ...jobAvailable.filter(item => item.dayOfWeek === 'THURSDAY')];
                      const friday = [...bookingResponseList.filter(item => item.dayOfWeek === 'FRIDAY'), ...jobAvailable.filter(item => item.dayOfWeek === 'FRIDAY')];
                      const saturday = [...bookingResponseList.filter(item => item.dayOfWeek === 'SATURDAY'), ...jobAvailable.filter(item => item.dayOfWeek === 'SATURDAY')];
                      const sunday = [...bookingResponseList.filter(item => item.dayOfWeek === 'SUNDAY'), ...jobAvailable.filter(item => item.dayOfWeek === 'SUNDAY')];

                      let totalTime = 0;
                      bookingResponseList.forEach(item => {
                        if (item.bookingStatus === 'DONE' && item.realEndTime && item.realStartTime) {
                          let dateStartTime = moment(item.realStartTime);
                          let dateEndTime = moment(item.realEndTime);
                          const number = parseFloat(moment(dateEndTime).diff(moment(dateStartTime), "hours", true).toFixed(2));
                          totalTime += number;
                        }

                      });
                      return (
                        <React.Fragment key={index}>
                          <Grid item className={classes.bodyLeft} style={{ width: '15%', borderBottom: index === dataList.length - 1 ? 'none' : 'solid 1px #d2d2d2' }} >{`${bookingByParticipant.clientResponse.firstName} ${bookingByParticipant.clientResponse.lastName}`}</Grid>
                          <Grid item className={classes.bodyItem} style={{ width: '11%', borderBottom: index === dataList.length - 1 ? 'none' : 'solid 1px #d2d2d2' }} >
                            {renderBodyItem(monday)}
                          </Grid>
                          <Grid item className={classes.bodyItem} style={{ width: '11%', borderBottom: index === dataList.length - 1 ? 'none' : 'solid 1px #d2d2d2' }} >
                            {renderBodyItem(tuesday)}
                          </Grid>
                          <Grid item className={classes.bodyItem} style={{ width: '11%', borderBottom: index === dataList.length - 1 ? 'none' : 'solid 1px #d2d2d2' }} >
                            {renderBodyItem(wednesday)}
                          </Grid>
                          <Grid item className={classes.bodyItem} style={{ width: '11%', borderBottom: index === dataList.length - 1 ? 'none' : 'solid 1px #d2d2d2' }} >
                            {renderBodyItem(thursday)}
                          </Grid>
                          <Grid item className={classes.bodyItem} style={{ width: '11%', borderBottom: index === dataList.length - 1 ? 'none' : 'solid 1px #d2d2d2' }} >
                            {renderBodyItem(friday)}
                          </Grid>
                          <Grid item className={classes.bodyItem} style={{ width: '11%', borderBottom: index === dataList.length - 1 ? 'none' : 'solid 1px #d2d2d2' }} >
                            {renderBodyItem(saturday)}
                          </Grid>
                          <Grid item className={classes.bodyItem} style={{ width: '11%', borderBottom: index === dataList.length - 1 ? 'none' : 'solid 1px #d2d2d2' }} >
                            {renderBodyItem(sunday)}
                          </Grid>
                          <Grid item className={classes.bodyRight} style={{ width: '8%', borderBottom: index === dataList.length - 1 ? 'none' : 'solid 1px #d2d2d2' }} >
                            {totalTime.toFixed(2)}
                          </Grid>
                        </React.Fragment>
                      )
                    })
                  }
                  {
                    !loadingPage && (!dataList || dataList.length === 0) &&
                    <Grid item xs={12} className={classes.bodyNodata}>
                      <span>{t("label.no_data")}</span>
                    </Grid>
                  }
                  {loadingPage && renderSkeleton()}

                </Grid>
              </PerfectScrollbar>
            </Grid>

            {/* Footer */}
            <Grid item className={`${classes.footerLeft} ${classes.footerItem}`} style={{ width: '15%', paddingLeft: 10 }} >{t('label.dailyTotal')}</Grid>
            <Grid item className={classes.footerItem} style={{ width: '11%' }} >{totalHour.monday.toFixed(2)}</Grid>
            <Grid item className={classes.footerItem} style={{ width: '11%' }} >{totalHour.tuesday.toFixed(2)}</Grid>
            <Grid item className={classes.footerItem} style={{ width: '11%' }} >{totalHour.wednesday.toFixed(2)}</Grid>
            <Grid item className={classes.footerItem} style={{ width: '11%' }} >{totalHour.thursday.toFixed(2)}</Grid>
            <Grid item className={classes.footerItem} style={{ width: '11%' }} >{totalHour.friday.toFixed(2)}</Grid>
            <Grid item className={classes.footerItem} style={{ width: '11%' }} >{totalHour.saturday.toFixed(2)}</Grid>
            <Grid item className={classes.footerItem} style={{ width: '11%' }} >{totalHour.sunday.toFixed(2)}</Grid>
            <Grid item className={`${classes.footerRight} ${classes.footerItem}`} style={{ width: '8%' }} >{totalHour.total.toFixed(2)}</Grid>
          </Grid>
        </div>
      </PerfectScrollbar>
      <AppDialog
        open={showBookingWorker}
        onClose={() => {
          // _loadData();
          // loadWorkersApplied();
          setShowBookingWorker(false);
          setSelectedWeek({});
          setJobId();
        }}
        maxWidth="md"
        title={t("label.booking")}
        actions={[
          {
            label: t('label.goToJobDetail'),
            onPress: () => { handleGoToDetail() },
          }
        ]}
      >
        {
          showBookingWorker &&
          <BookingWorkerModal
            jobId={jobId}
            loadJob={() => handleReloadJob()}
            jobDetail={initializeData}
            jobAvailabilities={jobAvailabilityList}
            // onAddShift={onAddShift}
            selectedWeek={selectedWeek}
            setSelectedWeek={setSelectedWeek}
            shiftType={shiftType}
            dayOfWeek={dayOfWeek}
            week={week}
            yearJobCalendar={yearJobCalendar}
            shiftId={shiftId}
            setShiftId={setShiftId}
            setShiftType-={setShiftType}
            setDayOfWeek={setDayOfWeek}
            setReload={setReload}
            reload={reload}
          />
        }
      </AppDialog>
    </div>
  );
};

CalendarTableShifts.propTypes = {
  firstWeekDay: PropTypes.any,
  dataList: PropTypes.array,
  loadingPage: PropTypes.number,
  totalPages: PropTypes.number,
  page: PropTypes.number,
  loadData: PropTypes.func,
};
export default CalendarTableShifts;
